/**
 * @typedef {Object} PropertyPhotoRecord
 * @property {string} key - S3 path with public/ prefix
 * @property {string} bucket
 * @property {string} region
 * @property {string} category
 * @property {boolean} isPrimary
 * @property {string} [note] - Optional description for open-ended damage/other photos
 */

/**
 * Strips inline WebP thumbnail data URLs from photo records before cache or server writes.
 * Persisted photos should be metadata-only; gallery tiles resolve URLs from `photo.key` at read time.
 *
 * @param {PropertyPhotoRecord[] | null | undefined} photos
 * @returns {PropertyPhotoRecord[] | null | undefined} Same input when not an array; otherwise metadata-only copies
 */
export function stripPhotoThumbnailsFromRecords(photos) {
  if (!Array.isArray(photos)) {
    // Choke-point callers guard with Array.isArray; leave null/undefined untouched.
    return photos;
  }

  return photos.map((photo) => {
    const { thumbnail: _inlineDataUrl, ...metadataOnlyRecord } = photo;
    return metadataOnlyRecord;
  });
}

/**
 * @param {Array<{ s3Path: string }>} uploaded
 * @param {Array<{ category?: string, isPrimary?: boolean, note?: string }>} rows
 * @param {{ bucketName: string, region: string }} bucketConfig
 * @returns {PropertyPhotoRecord[]}
 */
export function buildPropertyPhotoRecords(uploaded, rows, bucketConfig) {
  return uploaded.map((item, index) => {
    const row = rows[index] ?? {};
    return {
      key: item.s3Path,
      bucket: bucketConfig.bucketName,
      region: bucketConfig.region,
      category: row.category ?? "",
      isPrimary: Boolean(row.isPrimary),
      note: row.note ?? "",
    };
  });
}

/**
 * Appends new photos; when next includes a primary, clears primary on existing records.
 *
 * @param {PropertyPhotoRecord[] | null | undefined} existing
 * @param {PropertyPhotoRecord[]} next
 * @returns {PropertyPhotoRecord[]}
 */
export function mergePropertyPhotos(existing, next) {
  const existingList = Array.isArray(existing) ? existing : [];
  const nextList = Array.isArray(next) ? next : [];
  const nextHasPrimary = nextList.some((photo) => photo.isPrimary === true);

  const updatedExisting = nextHasPrimary
    ? existingList.map((photo) => ({ ...photo, isPrimary: false }))
    : existingList;

  return [...updatedExisting, ...nextList];
}

/**
 * @param {PropertyPhotoRecord[] | null | undefined} existing
 * @param {string} key - S3 path key to remove
 * @returns {PropertyPhotoRecord[]}
 */
export function removePropertyPhotoByKey(existing, key) {
  const existingList = Array.isArray(existing) ? existing : [];
  const target = String(key || "").trim();
  if (!target) return existingList;
  return existingList.filter((photo) => photo.key !== target);
}

/**
 * @param {PropertyPhotoRecord[] | null | undefined} existing
 * @param {string} key - S3 path key to update
 * @param {string} note
 * @returns {PropertyPhotoRecord[]}
 */
export function updatePropertyPhotoNoteByKey(existing, key, note) {
  const existingList = Array.isArray(existing) ? existing : [];
  const target = String(key || "").trim();
  if (!target) return existingList;
  return existingList.map((photo) => (photo.key === target ? { ...photo, note: note ?? "" } : photo));
}

/**
 * Promotes one photo to primary and clears primary on all others.
 *
 * @param {PropertyPhotoRecord[] | null | undefined} existing
 * @param {string} key - S3 path key to promote as primary
 * @returns {PropertyPhotoRecord[]}
 */
export function setPropertyPhotoPrimaryByKey(existing, key) {
  const existingList = Array.isArray(existing) ? existing : [];
  const target = String(key || "").trim();
  if (!target) return existingList;
  return existingList.map((photo) => ({
    ...photo,
    isPrimary: photo.key === target,
  }));
}
